import { Sparkles, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { EnhanceState } from "@/stores/storyWorkspaceStore";

type EnhanceSuggestionPanelProps = {
  onDismiss: () => void;
  onPromptAction?: () => void;
  onReplace: () => void;
  state: EnhanceState;
};

function hasReplaceableSuggestion(state: EnhanceState) {
  return Boolean(
    state.suggestion
      && state.originalText
      && state.suggestion.trim() !== state.originalText.trim(),
  );
}

export function EnhanceSuggestionPanel({
  onDismiss,
  onPromptAction,
  onReplace,
  state,
}: EnhanceSuggestionPanelProps) {
  const { t } = useTranslation();
  const canReplace = hasReplaceableSuggestion(state);

  if (state.status === "idle") {
    return null;
  }

  return (
    <section
      aria-live="polite"
      className="space-y-4 rounded-md border border-ink/10 bg-white/95 p-4 shadow-vellum"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-clay" />
            <h3 className="font-display text-xl leading-none text-foreground">
              {t("story.ai.enhance.title")}
            </h3>
            {typeof state.creditsRemaining === "number" ? (
              <Badge variant="outline">
                {t("story.ai.textEnhancement.creditsRemaining", {
                  count: state.creditsRemaining,
                })}
              </Badge>
            ) : null}
          </div>
          {state.sourceContext ? (
            <p className="text-xs leading-5 text-muted-foreground">
              {t("story.ai.enhance.sourceContext", {
                chapter: state.sourceContext.chapterTitle,
                genre: state.sourceContext.genre ?? t("story.ai.textEnhancement.unspecified"),
                language: state.sourceContext.language ?? t("story.ai.textEnhancement.unspecified"),
                styleGuide: state.sourceContext.styleGuideUsed
                  ? t("story.ai.textEnhancement.used")
                  : t("story.ai.textEnhancement.notUsed"),
              })}
            </p>
          ) : null}
        </div>

        <Button onClick={onDismiss} size="sm" type="button" variant="ghost">
          {t("story.ai.textEnhancement.dismiss")}
        </Button>
      </div>

      {state.status === "loading" ? (
        <p className="text-sm text-muted-foreground">
          {t("story.ai.enhance.loading")}
        </p>
      ) : null}

      {state.status === "error" ? (
        <div className="space-y-3 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          <p className="flex items-center gap-2 font-medium">
            <TriangleAlert className="h-4 w-4" />
            {state.error ? t(state.error) : t("story.ai.enhance.errors.failed")}
          </p>
          {state.prompt ? (
            <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
              {t(state.prompt.ctaLabelKey ?? "story.ai.textEnhancement.dismiss")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {state.noImprovements ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
          {t("story.ai.textEnhancement.noImprovements")}
        </p>
      ) : null}

      {state.suggestion ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {t("story.ai.textEnhancement.original")}
            </p>
            <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-ink/10 bg-[#fbf7ef] p-3 text-sm leading-6 text-foreground">
              {state.originalText}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {t("story.ai.textEnhancement.enhanced")}
            </p>
            <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-emerald-200 bg-emerald-50/80 p-3 text-sm leading-6 text-foreground">
              {state.suggestion}
            </div>
          </div>
        </div>
      ) : null}

      {state.suggestion ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onDismiss} type="button" variant="outline">
            {t("story.ai.textEnhancement.dismiss")}
          </Button>
          <Button disabled={!canReplace} onClick={onReplace} type="button">
            {t("story.ai.enhance.replace")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
